import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import useSEO from "@/hooks/useSEO";

const FALLBACK = "https://images.unsplash.com/photo-1714976694810-85add1a29c96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxlbXBhdGhldGljJTIwZG9jdG9yJTIwdGhlcmFwaXN0JTIwcGF0aWVudHxlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";

/**
 * ContentListPage — shared list page for Case Reports & Articles.
 */
export default function ContentListPage({
  type,           // "case_report" | "article"
  routeBase,      // "/case-reports" | "/articles"
  eyebrow,
  title,
  subtitle,
  seoTitle,
  seoDescription,
  testIdPrefix,   // "case" | "article"
}) {
  useSEO({ title: seoTitle, description: seoDescription });
  const [items, setItems] = useState(() => ssgData(`content:${type}`) || []);
  const [loading, setLoading] = useState(!ssgData(`content:${type}`));

  useEffect(() => {
    api
      .get(`/content?type=${type}`)
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <div data-testid={`${testIdPrefix}-list-page`}>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-12">
        <p className="eyebrow text-[#A39171] mb-6">{eyebrow}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          {title}
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">{subtitle}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        {loading ? (
          <p className="text-[#A39171]">Loading…</p>
        ) : items.length === 0 ? (
          <div className="bg-[#F4EFE6] rounded-3xl p-10 md:p-16 border border-[#A39171]/15 text-center">
            <p className="font-display text-2xl text-[#2D2623] mb-2">Nothing published yet.</p>
            <p className="text-[#5C524E]">Please check back soon — new pieces are added regularly by our clinical team.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p, i) => (
              <Link
                key={p.id}
                to={`${routeBase}/${p.slug}`}
                data-testid={`${testIdPrefix}-card-${i}`}
                className="group bg-[#F4EFE6] rounded-3xl overflow-hidden border border-[#A39171]/10 hover:shadow-[0_24px_60px_rgba(163,145,113,0.15)] transition-all duration-500 hover:-translate-y-1"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.cover_image || FALLBACK}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between mb-3">
                    <p className="eyebrow text-[#A39171]">{p.tag || (type === "case_report" ? "Case Report" : "Article")}</p>
                    <p className="text-xs text-[#A39171]">
                      {new Date(p.created_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                  <h3 className="font-display text-2xl text-[#2D2623] leading-tight tracking-tight group-hover:text-[#E07A5F] transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-[#5C524E] mt-3 line-clamp-3">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm text-[#2D2623] font-medium">
                    {p.external_url ? (
                      <>Read publication <ExternalLink className="w-3.5 h-3.5" /></>
                    ) : (
                      <>Read {type === "case_report" ? "case" : "article"} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>
                    )}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
